import React from "react";

const NonGstInvoiceLayout = ({ invoice, profile }) => {
    // Format currency values
    const formatCurrency = (value) => {
        return parseFloat(value || 0).toFixed(2);
    };

    // Format date values to dd-mm-yyyy
    const formatDate = (value) => {
        if (!value) return '';
        try {
            // Handle Date object
            const dateObj = value instanceof Date ? value : new Date(value);
            if (isNaN(dateObj.getTime())) {
                // Try splitting yyyy-mm-dd or yyyy/mm/dd
                const s = String(value).replace(/\//g, '-');
                const parts = s.split('-');
                if (parts.length === 3) {
                    const [y, m, d] = parts.map(p => p.padStart(2, '0'));
                    return `${d}-${m}-${y}`;
                }
                return String(value);
            }
            const dd = String(dateObj.getDate()).padStart(2, '0');
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const yyyy = String(dateObj.getFullYear());
            return `${dd}-${mm}-${yyyy}`;
        } catch {
            return String(value);
        }
    };

    // Default profile values if none provided
    const defaultProfile = {
        franchiseName: "TATYS",
        address: "NO 354, T H ROAD THIRUMAZHISAI - 600124",
        mobileNo: "8015695652",
        email: "thirumazhisai.dist@gmail.com",
        gstNo: "27CIOPP1874N1Z",
        panNo: "CIOPP1874N",
        bankName: "Canara Bank",
        accountNo: "102000820462",
        ifsc: "CNRB0003072",
        branchName: "THIRUMAZHISAI",
        terms: [
            "Payment should be made by cheque in favour of TATYS",
            "Any queries must be forwarded within 15 days of receipt",
            "18% interest will be charged on overdue bills"
        ]
    };

    // Use provided profile or default values
    const companyProfile = profile || defaultProfile;

    // Bookings pagination
    const itemsPerPage = 30;
    const allItems = Array.isArray(invoice.items) ? invoice.items : [];
    const chunkedItems = [];
    for (let i = 0; i < allItems.length; i += itemsPerPage) {
        chunkedItems.push(allItems.slice(i, i + itemsPerPage));
    }
    const bookingsPages = Math.max(1, chunkedItems.length || 1);
    const totalPages = 1 + bookingsPages; // 1 for bill page

    // Consignment/Base Amount derived from booking details (sum of item amount + item other)
    const consignmentAmountFromBookings = (allItems || []).reduce((sum, item) => {
        const amount = parseFloat(item?.amount || 0);
        const other = parseFloat(item?.other || 0);
        return sum + amount + other;
    }, 0);

    // Base (booking subtotal) from booking details (sum of item amounts only)
    const baseAmountFromBookings = (allItems || []).reduce((sum, item) => {
        const amount = parseFloat(item?.amount || 0);
        return sum + amount;
    }, 0);

    const derivedFuelPercent = baseAmountFromBookings > 0
        ? (parseFloat(invoice.fuelCharge || 0) / baseAmountFromBookings) * 100
        : 0;
    const derivedRoyaltyPercent = baseAmountFromBookings > 0
        ? (parseFloat(invoice.royaltyCharge || 0) / baseAmountFromBookings) * 100
        : 0;
    const displayFuelPercent = (invoice.fuelPercent != null && invoice.fuelPercent !== '' && !isNaN(parseFloat(invoice.fuelPercent))) ? parseFloat(invoice.fuelPercent) : derivedFuelPercent;
    const displayRoyaltyPercent = (invoice.royaltyPercent != null && invoice.royaltyPercent !== '' && !isNaN(parseFloat(invoice.royaltyPercent))) ? parseFloat(invoice.royaltyPercent) : derivedRoyaltyPercent;

    const taxableAmount = parseFloat(consignmentAmountFromBookings || 0) +
        parseFloat(invoice.odaCharge || 0) +
        parseFloat(invoice.docketCharge || 0) +
        parseFloat(invoice.otherCharge || 0) +
        parseFloat(invoice.fuelCharge || 0) +
        parseFloat(invoice.royaltyCharge || 0);

    return (
        <div className="mx-auto bg-white text-sm print:text-xs" style={{ margin: '10px' }}>
            {/* Page 1 - All Invoice Data */}
            <div className="page-break-after" style={{ border: '2px solid #000', padding: '15px', marginBottom: '10px' }}>
                {/* Header with Logo and Company Info */}
                <div className="border-2 border-gray-600 p-4 bg-white">
                    <div className="flex justify-between items-start">
                        {/* Company Information */}
                        <div className="flex-1 space-y-1">
                            <h1 className="text-2xl font-bold text-black">{companyProfile.franchiseName}</h1>
                            <p className="text-sm font-medium text-black">{companyProfile.address}</p>
                            <p className="text-sm text-black">Mobile: {companyProfile.mobileNo} | Email: {companyProfile.email}</p>
                            <p className="text-sm font-semibold text-black">GST No: {companyProfile.gstNo}</p>
                        </div>

                        {/* Logo Container */}
                        <div className="flex-shrink-0 ml-6">
                            <div className=" h-24 flex items-center justify-center">
                                <img
                                    src={companyProfile.logoUrl || '/logo.png'}
                                    alt="Company Logo"
                                    className="max-w-full max-h-full object-contain"
                                    crossOrigin="anonymous"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invoice Details Section */}
                <div className="grid grid-cols-2 gap-4 p-4 border-x-2 border-b-2 border-gray-600">
                    <div className="space-y-3">
                        <h3 className="text-lg font-bold text-black border-b-2 border-gray-600 pb-2">Bill To:</h3>
                        <div className="space-y-2">
                            <p className="font-semibold text-lg text-black">M/s {invoice.customer.name}</p>
                            {(() => {
                                let addr = invoice.customer.address || '';
                                addr = addr.slice(0, 100); // Limit to 100 characters
                                const lines = [];
                                for (let i = 0; i < addr.length; i += 50) {
                                    lines.push(addr.slice(i, i + 50));
                                }
                                return lines.map((line, idx) => (
                                    <p key={idx} className="text-black">{line}</p>
                                ));
                            })()}
                            <p className="font-medium text-black">GST No: {invoice.customer.gst}</p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h3 className="text-lg font-bold text-black border-b-2 border-gray-600 pb-2">Invoice Details:</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="font-medium text-black">Invoice No:</span>
                                <span className="font-bold text-black">{invoice.number}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-black">Invoice Date:</span>
                                <span className="text-black">{formatDate(invoice.date)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-black">Period:</span>
                                <span className="text-black">{formatDate(invoice.fromDate)} to {formatDate(invoice.toDate)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-black">Total Days:</span>
                                <span className="font-semibold text-black">{invoice.days} days</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Statistics */}
                <div className="grid grid-cols-4 gap-3 p-4 border-x-2 border-b-2 border-gray-600 bg-gray-50">
                    <div className="text-center p-3 bg-white rounded border-2 border-gray-600">
                        <p className="text-2xl font-bold text-black">{invoice.consignment}</p>
                        <p className="text-sm text-black">Total Consignments</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded border-2 border-gray-600">
                        <p className="text-2xl font-bold text-black">{invoice.weight} kg</p>
                        <p className="text-sm text-black">Total Weight</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded border-2 border-gray-600">
                        <p className="text-2xl font-bold text-black">{invoice.days}</p>
                        <p className="text-sm text-black">Invoice Days</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded border-2 border-gray-600">
                        <p className="text-2xl font-bold text-black">₹{formatCurrency(taxableAmount)}</p>
                        <p className="text-sm text-black">Taxable Amount</p>
                    </div>
                </div>

                {/* Charges Breakdown */}
                <div className="p-4 border-x-2 border-b-2 border-gray-600">
                    <h3 className="text-lg font-bold text-black mb-3 border-b-2 border-gray-600 pb-2">Charges Breakdown:</h3>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Consignment Amount:</span>
                                <span className="font-semibold text-black">₹{formatCurrency(consignmentAmountFromBookings)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">ODA Charges:</span>
                                <span className="text-black">₹{formatCurrency(invoice.odaCharge || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Docket Charges:</span>
                                <span className="text-black">₹{formatCurrency(invoice.docketCharge || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Other Charges:</span>
                                <span className="text-black">₹{formatCurrency(invoice.otherCharge || 0)}</span>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Fuel Charges ({displayFuelPercent.toFixed(2)}%):</span>
                                <span className="text-black">₹{formatCurrency(invoice.fuelCharge || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Royalty Charges ({displayRoyaltyPercent.toFixed(2)}%):</span>
                                <span className="text-black">₹{formatCurrency(invoice.royaltyCharge || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b-2 border-gray-500">
                                <span className="font-medium text-black">Total Value:</span>
                                <span className="font-semibold text-black">₹{formatCurrency(
                                    parseFloat(consignmentAmountFromBookings || 0) +
                                    parseFloat(invoice.odaCharge || 0) +
                                    parseFloat(invoice.docketCharge || 0) +
                                    parseFloat(invoice.otherCharge || 0) +
                                    parseFloat(invoice.fuelCharge || 0) +
                                    parseFloat(invoice.royaltyCharge || 0)
                                )}</span>
                            </div>
                        </div>
                    </div>

                    {/* Total Amount */}
                    <div className="mt-4 p-3 bg-gray-100 rounded border-2 border-gray-600">
                        <div className="flex justify-between items-center">
                            <span className="text-xl font-bold text-black">GRAND TOTAL:</span>
                            <span className="text-2xl font-bold text-black">₹{formatCurrency(invoice.total)}</span>
                        </div>
                    </div>
                </div>

                {/* Terms and Bank Details */}
                <div className="grid grid-cols-2 gap-4 p-4 border-x-2 border-b-2 border-gray-600">
                    <div className="space-y-3 flex flex-col">
                        <h3 className="text-lg font-bold text-black border-b-2 border-gray-600 pb-2">Terms & Conditions:</h3>
                        <div className="space-y-2 text-sm flex-1 overflow-hidden">
                            <div className="h-full overflow-hidden">
                                {(companyProfile.terms || []).map((term, index) => (
                                    <p key={index} className="text-black break-words">• {term}</p>
                                ))}
                                <p className="font-semibold mt-2 text-black">PAN: {companyProfile.panNo}</p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3 flex flex-col">
                        <h3 className="text-lg font-bold text-black border-b-2 border-gray-600 pb-2">Bank Details:</h3>
                        <div className="space-y-2 text-sm flex-1">
                            <p className="text-black"><span className="font-medium">Bank:</span> {companyProfile.bankName}</p>
                            <p className="text-black"><span className="font-medium">A/C No:</span> {companyProfile.accountNo}</p>
                            <p className="text-black"><span className="font-medium">IFSC:</span> {companyProfile.ifsc}</p>
                            <p className="text-black"><span className="font-medium">Branch:</span> {companyProfile.branchName}</p>
                        </div>
                    </div>
                </div>

                {/* Signature and Page Info */}
                <div className="flex justify-between items-end p-4 border-x-2 border-b-2 border-gray-600">
                    <div className="text-center">
                        <div className="w-28 h-20 mx-auto mb-1 border-2 border-gray-600 rounded flex items-center justify-center text-xs text-gray-500 bg-gray-50">
                            Authorized Signature
                        </div>
                        <p className="text-sm font-medium text-black">Authorized Signatory</p>
                    </div>
                    <div className="text-right text-sm">
                        <p className="font-semibold text-black">Page 1 of {totalPages}</p>
                    </div>
                </div>
            </div>

            {/* Bookings pages */}
            {chunkedItems.map((pageItems, pageIndex) => (
                <div key={pageIndex} className="page-break-before" style={{ border: '2px solid #000', padding: '20px' }}>
                    {/* Header */}
                    <div className="border-2 border-gray-600 p-4 bg-gray-50">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold text-black">INVOICE DETAILS</h2>
                                <p className="text-sm text-black">Invoice No: {invoice.number} | Customer: {invoice.customer.name}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-black">Page {pageIndex + 2} of {totalPages}</p>
                            </div>
                        </div>
                    </div>

                    {/* Bookings Table */}
                    <div className="border-x-2 border-b-2 border-gray-600">
                        <table className="w-full border-collapse text-xs">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Sr.</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Date</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">AWB No.</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Receiver</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Destination</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Mode</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Weight</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Qty</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Type</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Amount</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Other</th>
                                    <th className="border-2 border-gray-600 p-2 text-center font-bold text-black">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((item, i) => {
                                    const globalIndex = pageIndex * itemsPerPage + i;
                                    return (
                                        <tr key={globalIndex} className={globalIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="border-2 border-gray-600 p-2 text-center font-medium text-black">{globalIndex + 1}</td>
                                            <td className="border-2 border-gray-600 p-2 text-black">{item.date}</td>
                                            <td className="border-2 border-gray-600 p-2 font-medium text-black">{item.cnote}</td>
                                            <td className="border-2 border-gray-600 p-2 text-black">{item.receiver}</td>
                                            <td className="border-2 border-gray-600 p-2 text-black">{item.destination}</td>
                                            <td className="border-2 border-gray-600 p-2 text-center text-black">{item.mode}</td>
                                            <td className="border-2 border-gray-600 p-2 text-right text-black">{item.weight}</td>
                                            <td className="border-2 border-gray-600 p-2 text-center text-black">{item.qty}</td>
                                            <td className="border-2 border-gray-600 p-2 text-center text-black">{item.type}</td>
                                            <td className="border-2 border-gray-600 p-2 text-right font-medium text-black">₹{formatCurrency(item.amount)}</td>
                                            <td className="border-2 border-gray-600 p-2 text-right text-black">₹{formatCurrency(item.other || 0)}</td>
                                            <td className="border-2 border-gray-600 p-2 text-right font-bold text-black">₹{formatCurrency(parseFloat(item.amount) + parseFloat(item.other || 0))}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {pageIndex === chunkedItems.length - 1 && (
                                <tfoot>
                                    <tr className="bg-gray-100 font-bold">
                                        <td colSpan="9" className="border-2 border-gray-600 p-2 text-right text-lg text-black">TOTAL:</td>
                                        <td className="border-2 border-gray-600 p-2 text-right text-lg text-black">₹{formatCurrency(allItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0))}</td>
                                        <td className="border-2 border-gray-600 p-2 text-right text-lg text-black">₹{formatCurrency(allItems.reduce((sum, item) => sum + parseFloat(item.other || 0), 0))}</td>
                                        <td className="border-2 border-gray-600 p-2 text-right text-lg text-black">₹{formatCurrency(allItems.reduce((sum, item) => sum + parseFloat(item.amount || 0) + parseFloat(item.other || 0), 0))}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="border-x-2 border-b-2 border-gray-600 p-4 bg-gray-50">
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-black">
                                <p>Total Consignments: <span className="font-semibold">{invoice.consignment}</span></p>
                                <p>Total Weight: <span className="font-semibold">{invoice.weight} kg</span></p>
                            </div>
                            <div className="text-right text-sm">
                                <p className="font-semibold text-black">Page {pageIndex + 2} of {totalPages}</p>
                                <p className="text-black">{pageIndex === chunkedItems.length - 1 ? 'End of Invoice' : 'Continued...'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NonGstInvoiceLayout;
